import React from "react";
import { motion } from "framer-motion";
import { Users, RefreshCw, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useBusiness } from "@/context/BusinessContext";
import { Panel, Spinner, SectionTitle, Button } from "@/components/ui";
import MemoryCard from "@/components/MemoryCard";

export default function Customers() {
  const { active, loading } = useBusiness();
  const [list, setList] = React.useState(null);
  const [open, setOpen] = React.useState(null);

  const load = React.useCallback(async () => {
    if (!active) return;
    const { data } = await api.get(`/businesses/${active.id}/customers`);
    setList(data);
  }, [active]);

  React.useEffect(() => { load(); }, [load]);

  if (loading) return null;
  if (!active) return <Panel className="p-8 text-center text-sm" style={{ color: "var(--text-2)" }}>Pick a workspace.</Panel>;

  return (
    <div>
      <SectionTitle eyebrow={active.name} title="Customers">
        <Button variant="ghost" size="sm" onClick={load} data-testid="customers-refresh"><RefreshCw size={14} /> Refresh</Button>
      </SectionTitle>

      {!list && <Panel className="p-8 flex justify-center"><Spinner /></Panel>}

      {list && list.length === 0 && (
        <Panel className="p-10 text-center" data-testid="customers-empty">
          <Users size={28} className="mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
          <p className="text-sm" style={{ color: "var(--text-2)" }}>No customers yet. As people message your number, Wappy builds a memory profile for each — facts, preferences, and behavior signals.</p>
        </Panel>
      )}

      {list && list.length > 0 && (
        <div className="space-y-px" style={{ background: "var(--border)" }} data-testid="customers-list">
          {list.map((c, i) => (
            <motion.div key={c.waId} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
              style={{ background: "var(--s1)" }}>
              <button onClick={() => setOpen(open === c.waId ? null : c.waId)} data-testid={`customer-${c.waId}`}
                className="w-full text-left p-4 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{c.name || `+${c.phone}`}</div>
                  <div className="mono text-[11px]" style={{ color: "var(--text-muted)" }}>
                    +{c.phone}{c.memory?.communicationStyle ? ` · ${c.memory.communicationStyle}` : ""}
                  </div>
                </div>
                <ChevronRight size={16} style={{ color: "var(--text-muted)", transform: open === c.waId ? "rotate(90deg)" : "none", transition: "transform .15s" }} />
              </button>
              {open === c.waId && (
                <div className="px-4 pb-4">
                  {c.memory ? <MemoryCard memory={c.memory} /> : <p className="text-xs" style={{ color: "var(--text-muted)" }}>No memory captured yet.</p>}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
